import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapControls from './MapControls';
import LayersPanel from './LayersPanel';

// Status colors matching the existing theme
const STATUS_COLORS = {
  proposed: '#50E3C2',
  approved: '#08B2E3',
  under_construction: '#F8E71C',
  completed: '#005E7C',
  cancelled: '#F93950',
};

// Default camera padding used when framing a set of projects.
const FIT_PADDING = { top: 60, bottom: 60, left: 60, right: 60 };
const NO_PADDING = { top: 0, bottom: 0, left: 0, right: 0 };

// Minimum height (px) of map that must remain between the top and bottom
// camera padding when framing. Below this Mapbox refuses to fit the bounds
// ("Map cannot fit within canvas...") and the zoom would be useless anyway.
const MIN_FIT_HEIGHT = 120;

// How far the top padding may shrink (px) to make room for a raised bottom
// padding before the bottom padding itself is reduced.
const MIN_TOP_PADDING = 20;

/**
 * Resolve the camera padding for fitToProjects.
 *
 * A caller may raise `requested.bottom` when something covers the lower part
 * of the map (the mobile filters sheet, see #311) so the projects are framed
 * in the strip that is still visible. The request is ignored when that strip
 * is shorter than MIN_FIT_HEIGHT (normal framing is then the best we can do).
 * Otherwise, if the default top padding plus the request leaves less than
 * MIN_FIT_HEIGHT, room is made by shrinking the top padding first and only
 * then the bottom one, so pins stay clear of the obstruction for as long as
 * possible.
 */
function resolveFitPadding(requested, mapHeight) {
  const padding = { ...FIT_PADDING };
  const bottom = Number(requested && requested.bottom);
  if (!Number.isFinite(bottom) || bottom <= padding.bottom) return padding;
  if (mapHeight - bottom < MIN_FIT_HEIGHT) return padding;

  padding.bottom = Math.round(bottom);
  const overflow = padding.top + padding.bottom + MIN_FIT_HEIGHT - mapHeight;
  if (overflow > 0) {
    const fromTop = Math.min(overflow, padding.top - MIN_TOP_PADDING);
    padding.top -= fromTop;
    padding.bottom -= overflow - fromTop;
  }
  return padding;
}

const MapView = forwardRef(function MapView({
  projects,
  center,
  mapboxToken,
  mapboxStyle,
  onBoundsChange,
  availableLayers = [],
  layerVisibility = {},
  onLayerToggle,
}, ref) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);
  const [is3D, setIs3D] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const loadedLayersRef = useRef(new Set());
  const orbitAnimationRef = useRef(null);
  const orbitCenterRef = useRef(null);

  // Helper function to generate popup HTML (DRY)
  const createPopupHTML = useCallback((data, includeImage = false) => {
    const articleCount = data.article_count || 0;
    const uses = Array.isArray(data.uses) ? data.uses.join(' / ') : (data.uses || '');
    const neighborhood = data.neighborhood || '';
    const metaSeparator = uses && neighborhood ? ' · ' : '';

    return `
      <div class="project-popup-content">
        ${includeImage && data.image_url ? `<div class="project-popup-image" style="background-image: url('${data.image_url}')"></div>` : ''}
        <div class="project-popup-body">
          <span class="project-popup-status status-${data.status}">${data.status_label}</span>
          <h3 class="project-popup-title">${data.title}</h3>
          <p class="project-popup-meta">${uses}${metaSeparator}${neighborhood}</p>
          <div class="project-popup-footer">
            ${articleCount > 0 ? `<span class="project-popup-article-count">${articleCount} article${articleCount !== 1 ? 's' : ''}</span>` : '<span></span>'}
            <a href="${data.url}" target="_blank" rel="noopener noreferrer" class="project-popup-link">View Details →</a>
          </div>
        </div>
      </div>
    `;
  }, []);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    focusOnProject: (project) => {
      if (!map.current || !mapLoaded) return;

      // Stop any existing orbit
      stopOrbit();

      const coordinates = [project.lon, project.lat];
      orbitCenterRef.current = coordinates;

      // Switch to 3D mode
      setIs3D(true);

      // Fly to location with 3D view
      map.current.flyTo({
        center: coordinates,
        zoom: 17,
        pitch: 60,
        bearing: 0,
        duration: 2000,
      });

      // After fly animation completes, show popup and start orbit
      setTimeout(() => {
        showProjectPopup(project);
        startOrbit(coordinates);
      }, 2000);
    },
    highlightProject: async (project) => {
      if (!map.current || !mapLoaded) return;

      // Stop any existing orbit
      stopOrbit();

      // Close any existing popup immediately
      if (popup.current) {
        popup.current.remove();
      }

      const coordinates = [project.lon, project.lat];
      const currentZoom = map.current.getZoom();

      // Start fetching project details and preloading image in parallel with map animation
      const dataPromise = (async () => {
        let projectData = { ...project };
        try {
          const response = await fetch(`/map/api/${project.nid}`);
          if (response.ok) {
            const fullDetails = await response.json();
            // Preserve article_count from initial data if not in fullDetails
            if (!fullDetails.article_count && project.article_count) {
              fullDetails.article_count = project.article_count;
            }
            projectData = fullDetails;
          }
        } catch (err) {
          console.warn('Failed to load project details:', err);
        }
        return projectData;
      })();

      // Preload image in parallel
      const imagePromise = (async () => {
        const imageUrl = project.image_url;
        if (imageUrl) {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = imageUrl;
          });
        }
        return false;
      })();

      // Wait for map animation to complete
      const mapPromise = new Promise((resolve) => {
        map.current.once('moveend', resolve);
      });

      // Start the map animation
      // Add top padding to account for popup height above the pin
      map.current.flyTo({
        center: coordinates,
        zoom: Math.max(currentZoom, 14),
        duration: 800,
        padding: { top: 150, bottom: 0, left: 0, right: 0 },
      });

      // Wait for all three: map animation, data fetch, and image preload
      const [projectData, imageLoaded] = await Promise.all([
        dataPromise,
        imagePromise,
        mapPromise,
      ]);

      // Now show the popup with everything ready
      const popupContent = document.createElement('div');
      popupContent.className = 'project-popup';
      popupContent.innerHTML = createPopupHTML(projectData, imageLoaded && !!projectData.image_url);

      popup.current
        .setLngLat(coordinates)
        .setDOMContent(popupContent)
        .addTo(map.current);
    },
    stopOrbit: () => {
      stopOrbit();
    },
    flyTo: (lon, lat) => {
      if (!map.current || !mapLoaded) return;
      map.current.flyTo({
        center: [lon, lat],
        zoom: 15,
        duration: 1500,
      });
    },
    // Pan/zoom the map so the given projects are all visible within the
    // viewport, and in the same movement flatten to a top-down 2D view.
    // Used when a saved search is loaded on the map/split layouts and
    // whenever a filter changes.
    //
    // `options.padding` may carry a larger `bottom` when part of the map is
    // covered (the mobile filters sheet, see #311); the projects are then
    // framed within the visible strip above it. See resolveFitPadding for
    // how the value is clamped.
    fitToProjects: (projectList, options = {}) => {
      if (!map.current || !mapLoaded) return;

      // Stop any orbit animation and reset the 2D/3D toggle to 2D.
      stopOrbit();
      setIs3D(false);

      // Collect valid coordinates from the provided projects.
      const points = (projectList || [])
        .map((p) => [Number(p.lon), Number(p.lat)])
        .filter(([lon, lat]) => Number.isFinite(lon) && Number.isFinite(lat));

      // No results: flatten the camera without changing the framing.
      if (points.length === 0) {
        map.current.easeTo({ pitch: 0, bearing: 0, duration: 800 });
        return;
      }

      const padding = resolveFitPadding(
        options.padding,
        map.current.getContainer().clientHeight,
      );

      // The padding is applied through `offset` (where the centre of the
      // padded area sits relative to the canvas centre) and the map's own
      // padding is reset to zero, rather than handing `padding` to the camera
      // call. In Mapbox GL 3 camera padding is sticky on the map's transform:
      // it would keep shifting every later camera movement (cluster clicks,
      // 2D/3D toggle...) and shrink getBounds(), which feeds the project
      // count, long after the filters sheet is gone.
      const offset = [
        (padding.left - padding.right) / 2,
        (padding.top - padding.bottom) / 2,
      ];

      // Single result: center on it at a sensible zoom, flattened.
      if (points.length === 1) {
        map.current.easeTo({
          center: points[0],
          zoom: 14,
          pitch: 0,
          bearing: 0,
          duration: 800,
          padding: NO_PADDING,
          offset,
        });
        return;
      }

      // Multiple results: fit bounds around them. cameraForBounds gives the
      // zoom that fits the bounds inside the padded area and the bounds'
      // centre; flyTo accepts pitch and bearing, so the flatten-to-top-down
      // happens in the same movement.
      const bounds = points.reduce(
        (acc, coord) => acc.extend(coord),
        new mapboxgl.LngLatBounds(points[0], points[0]),
      );
      const camera = map.current.cameraForBounds(bounds, {
        padding,
        maxZoom: 15,
        pitch: 0,
        bearing: 0,
      });
      if (!camera) return;

      map.current.flyTo({
        center: camera.center,
        zoom: camera.zoom,
        pitch: 0,
        bearing: 0,
        duration: 800,
        padding: NO_PADDING,
        offset,
      });
    },
  }), [mapLoaded]);

  // Show popup for a project (with async detail loading)
  const showProjectPopup = useCallback(async (project) => {
    if (!map.current || !popup.current) return;

    const coordinates = [project.lon, project.lat];

    // Show basic popup immediately
    const popupContent = document.createElement('div');
    popupContent.className = 'project-popup';
    popupContent.innerHTML = createPopupHTML(project, false);

    popup.current
      .setLngLat(coordinates)
      .setDOMContent(popupContent)
      .addTo(map.current);

    // Fetch full details and update popup
    try {
      const response = await fetch(`/map/api/${project.nid}`);
      if (response.ok) {
        const fullDetails = await response.json();
        // Preserve article_count from initial data if not in fullDetails
        if (!fullDetails.article_count && project.article_count) {
          fullDetails.article_count = project.article_count;
        }
        if (popup.current.isOpen()) {
          const updatedContent = document.createElement('div');
          updatedContent.className = 'project-popup';
          updatedContent.innerHTML = createPopupHTML(fullDetails, true);
          popup.current.setDOMContent(updatedContent);
        }
      }
    } catch (err) {
      console.warn('Failed to load project details:', err);
    }
  }, [createPopupHTML]);

  // Start orbit animation around a point
  const startOrbit = useCallback((coordinates) => {
    if (!map.current) return;

    const orbitDuration = 20000; // 20 seconds per full orbit
    const startTime = Date.now();
    const startBearing = map.current.getBearing();

    const animate = () => {
      if (!map.current || !orbitCenterRef.current) return;

      const elapsed = Date.now() - startTime;
      const progress = (elapsed % orbitDuration) / orbitDuration;
      const bearing = startBearing + (progress * 360);

      map.current.setBearing(bearing);
      map.current.setCenter(coordinates);

      orbitAnimationRef.current = requestAnimationFrame(animate);
    };

    orbitAnimationRef.current = requestAnimationFrame(animate);
  }, []);

  // Stop orbit animation
  const stopOrbit = useCallback(() => {
    if (orbitAnimationRef.current) {
      cancelAnimationFrame(orbitAnimationRef.current);
      orbitAnimationRef.current = null;
    }
    orbitCenterRef.current = null;
  }, []);

  // Stop orbit on user interaction
  useEffect(() => {
    if (!map.current) return;

    const handleInteraction = () => {
      if (orbitAnimationRef.current) {
        stopOrbit();
      }
    };

    map.current.on('dragstart', handleInteraction);
    map.current.on('zoomstart', handleInteraction);
    map.current.on('rotatestart', handleInteraction);

    return () => {
      if (map.current) {
        map.current.off('dragstart', handleInteraction);
        map.current.off('zoomstart', handleInteraction);
        map.current.off('rotatestart', handleInteraction);
      }
    };
  }, [stopOrbit]);

  // Initialize map
  useEffect(() => {
    if (!mapboxToken || map.current) return;

    mapboxgl.accessToken = mapboxToken;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapboxStyle || 'mapbox://styles/mapbox/light-v11',
      center: [center.lon, center.lat],
      zoom: 11,
      pitch: 0,
      bearing: 0,
    });

    // Create popup instance
    popup.current = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      maxWidth: '320px',
      offset: 15,
    });

    // Add navigation controls (but we'll use our custom ones too)
    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');

    // Handle map load
    map.current.on('load', () => {
      add3DBuildingsLayer();
      addProjectsLayer();
      setupEventHandlers();
      emitBoundsChange();
      setMapLoaded(true);
    });

    // Cleanup
    return () => {
      stopOrbit();
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapboxToken, mapboxStyle]);

  // ResizeObserver - automatically resize map when container size changes
  useEffect(() => {
    if (!mapContainer.current) return;

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (map.current) {
          map.current.resize();
        }
      });
    });

    resizeObserver.observe(mapContainer.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Handle layer visibility changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    availableLayers.forEach((layerConfig) => {
      const isVisible = layerVisibility[layerConfig.id] || false;
      const isLoaded = loadedLayersRef.current.has(layerConfig.id);

      if (isVisible && !isLoaded) {
        loadGeoJSONLayer(layerConfig);
      } else if (!isVisible && isLoaded) {
        hideLayer(layerConfig.id);
      } else if (isVisible && isLoaded) {
        showLayer(layerConfig.id);
      }
    });
  }, [layerVisibility, mapLoaded, availableLayers]);

  // Load a GeoJSON layer
  const loadGeoJSONLayer = async (layerConfig) => {
    if (!map.current) return;

    try {
      const response = await fetch(layerConfig.source);
      if (!response.ok) throw new Error(`Failed to load ${layerConfig.source}`);
      const geojsonData = await response.json();

      if (!map.current.getSource(layerConfig.id)) {
        map.current.addSource(layerConfig.id, {
          type: 'geojson',
          data: geojsonData,
        });
      }

      if (layerConfig.fillStyle) {
        const fillLayerId = `${layerConfig.id}-fill`;
        if (!map.current.getLayer(fillLayerId)) {
          map.current.addLayer({
            id: fillLayerId,
            source: layerConfig.id,
            type: layerConfig.fillStyle.type,
            paint: layerConfig.fillStyle.paint,
          }, 'clusters');
        }
      }

      if (!map.current.getLayer(layerConfig.id)) {
        map.current.addLayer({
          id: layerConfig.id,
          source: layerConfig.id,
          type: layerConfig.style.type,
          paint: layerConfig.style.paint,
        }, 'clusters');
      }

      if (layerConfig.labelStyle) {
        const labelLayerId = `${layerConfig.id}-label`;
        if (!map.current.getLayer(labelLayerId)) {
          map.current.addLayer({
            id: labelLayerId,
            source: layerConfig.id,
            type: layerConfig.labelStyle.type,
            layout: layerConfig.labelStyle.layout,
            paint: layerConfig.labelStyle.paint,
          });
        }
      }

      loadedLayersRef.current.add(layerConfig.id);
    } catch (error) {
      console.error(`Error loading layer ${layerConfig.id}:`, error);
    }
  };

  // Hide a layer
  const hideLayer = (layerId) => {
    if (!map.current) return;

    if (map.current.getLayer(layerId)) {
      map.current.setLayoutProperty(layerId, 'visibility', 'none');
    }
    if (map.current.getLayer(`${layerId}-fill`)) {
      map.current.setLayoutProperty(`${layerId}-fill`, 'visibility', 'none');
    }
    if (map.current.getLayer(`${layerId}-label`)) {
      map.current.setLayoutProperty(`${layerId}-label`, 'visibility', 'none');
    }
  };

  // Show a layer
  const showLayer = (layerId) => {
    if (!map.current) return;

    if (map.current.getLayer(layerId)) {
      map.current.setLayoutProperty(layerId, 'visibility', 'visible');
    }
    if (map.current.getLayer(`${layerId}-fill`)) {
      map.current.setLayoutProperty(`${layerId}-fill`, 'visibility', 'visible');
    }
    if (map.current.getLayer(`${layerId}-label`)) {
      map.current.setLayoutProperty(`${layerId}-label`, 'visibility', 'visible');
    }
  };

  // Update center when it changes
  useEffect(() => {
    if (map.current && center) {
      map.current.flyTo({
        center: [center.lon, center.lat],
        zoom: 11,
        duration: 1000,
      });
    }
  }, [center]);

  // Add/update projects layer when projects change
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    const source = map.current.getSource('projects');
    if (source) {
      source.setData(createGeoJSON());
    }
  }, [projects]);

  // Add 3D buildings layer
  const add3DBuildingsLayer = useCallback(() => {
    if (!map.current) return;

    const layers = map.current.getStyle().layers;
    const labelLayerId = layers.find(
      (layer) => layer.type === 'symbol' && layer.layout['text-field']
    )?.id;

    map.current.addLayer(
      {
        id: '3d-buildings',
        source: 'composite',
        'source-layer': 'building',
        filter: ['==', 'extrude', 'true'],
        type: 'fill-extrusion',
        minzoom: 14,
        paint: {
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-height': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14,
            0,
            14.5,
            ['get', 'height'],
          ],
          'fill-extrusion-base': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14,
            0,
            14.5,
            ['get', 'min_height'],
          ],
          'fill-extrusion-opacity': 0.6,
        },
      },
      labelLayerId
    );
  }, []);

  // Create GeoJSON from projects
  const createGeoJSON = useCallback(() => {
    return {
      type: 'FeatureCollection',
      features: projects.map((project) => ({
        type: 'Feature',
        properties: {
          nid: project.nid,
          title: project.title,
          url: project.url,
          status: project.status,
          status_label: project.status_label,
          uses: project.uses?.join(' / ') || '',
          neighborhood: project.neighborhood,
          image_url: project.image_url,
          article_count: project.article_count || 0,
        },
        geometry: {
          type: 'Point',
          coordinates: [project.lon, project.lat],
        },
      })),
    };
  }, [projects]);

  // Add projects layer with clustering
  const addProjectsLayer = useCallback(() => {
    if (!map.current) return;

    map.current.addSource('projects', {
      type: 'geojson',
      data: createGeoJSON(),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });

    map.current.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'projects',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#08B2E3',
          10, '#50E3C2',
          50, '#005E7C',
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          10, 25,
          50, 35,
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    map.current.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'projects',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 14,
      },
      paint: {
        'text-color': '#fff',
      },
    });

    map.current.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'match',
          ['get', 'status'],
          'proposed', STATUS_COLORS.proposed,
          'approved', STATUS_COLORS.approved,
          'under_construction', STATUS_COLORS.under_construction,
          'completed', STATUS_COLORS.completed,
          'cancelled', STATUS_COLORS.cancelled,
          '#08B2E3',
        ],
        'circle-radius': 10,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });
  }, [createGeoJSON]);

  // Setup event handlers
  const setupEventHandlers = useCallback(() => {
    if (!map.current) return;

    map.current.on('click', 'clusters', (e) => {
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      const clusterId = features[0].properties.cluster_id;
      map.current.getSource('projects').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.current.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
        });
      });
    });

    map.current.on('click', 'unclustered-point', async (e) => {
      // Stop any orbit animation when clicking on a point
      stopOrbit();

      const feature = e.features[0];
      const coordinates = feature.geometry.coordinates.slice();
      const properties = feature.properties;

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      setSelectedProject(properties);

      // Show popup with basic info immediately (using shared helper)
      const popupContent = document.createElement('div');
      popupContent.className = 'project-popup';
      popupContent.innerHTML = createPopupHTML(properties, false);

      popup.current
        .setLngLat(coordinates)
        .setDOMContent(popupContent)
        .addTo(map.current);

      // Fetch full details in background and update popup if still open
      try {
        const response = await fetch(`/map/api/${properties.nid}`);
        if (response.ok) {
          const fullDetails = await response.json();
          // Preserve article_count from initial data if not in fullDetails
          if (!fullDetails.article_count && properties.article_count) {
            fullDetails.article_count = properties.article_count;
          }
          if (popup.current.isOpen()) {
            const updatedContent = document.createElement('div');
            updatedContent.className = 'project-popup';
            updatedContent.innerHTML = createPopupHTML(fullDetails, true);
            popup.current.setDOMContent(updatedContent);
          }
        }
      } catch (err) {
        console.warn('Failed to load project details:', err);
      }
    });

    map.current.on('mouseenter', 'clusters', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current.on('mouseleave', 'clusters', () => {
      map.current.getCanvas().style.cursor = '';
    });
    map.current.on('mouseenter', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current.on('mouseleave', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = '';
    });

    map.current.on('moveend', emitBoundsChange);
  }, [stopOrbit, createPopupHTML]);

  // Emit current bounds
  const emitBoundsChange = useCallback(() => {
    if (!map.current || !onBoundsChange) return;

    const bounds = map.current.getBounds();
    onBoundsChange({
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
    });
  }, [onBoundsChange]);

  // Handle zoom controls
  const handleZoomIn = useCallback(() => {
    if (map.current) {
      map.current.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (map.current) {
      map.current.zoomOut();
    }
  }, []);

  // Handle 2D/3D toggle
  const handleToggle3D = useCallback(() => {
    if (!map.current) return;

    const new3D = !is3D;
    setIs3D(new3D);

    map.current.easeTo({
      pitch: new3D ? 45 : 0,
      bearing: 0,
      duration: 500,
    });
  }, [is3D]);

  return (
    <div className="map-view">
      <div ref={mapContainer} className="map-container" />
      <MapControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onToggle3D={handleToggle3D}
        is3D={is3D}
      />
      <LayersPanel
        layers={availableLayers}
        layerVisibility={layerVisibility}
        onLayerToggle={onLayerToggle}
      />
    </div>
  );
});

export default MapView;
